//store/usePermissionStore.js
import { defineStore } from "pinia";
import * as permissionService from "@/services/permissionService";

export const usePermissionStore = defineStore("permission", {
  state: () => ({
    permissions: [],
    selectedPermission: null,
    selectedPermissionIds: [],
    idPermission: null,
    error: null,
    totalPermissionsCount: 0,
    totalPermissionsPages: 1,
    currentPermissionsPage: 1,
    permissionsPageSize: 10,
    permissionSearchTerm: "",
    shouldForcePermissionsPageOne: false,
  }),

  getters: {
    isAllPermissionsSelected: (state) => (total) =>
      state.selectedPermissionIds.length === total,
  },

  actions: {
    setIdPermission(id) {
      this.idPermission = id;
    },

    setSelectedPermission(permission) {
      this.selectedPermission = permission;
    },

    togglePermissionSelection(id) {
      const index = this.selectedPermissionIds.indexOf(id);
      if (index > -1) {
        this.selectedPermissionIds.splice(index, 1);
      } else {
        this.selectedPermissionIds.push(id);
      }
    },

    clearPermissionSelection() {
      this.selectedPermissionIds = [];
    },

    async fetchAllPermissions() {
      try {
        const response = await permissionService.fetchPermissions();
        this.permissions = response.data;
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error(
          "❌ Erro ao buscar permissões (não paginado):",
          this.error
        );
        throw new Error(this.error);
      }
    },

    async fetchPermissionsPaged(pageNumber = 1, pageSize = 10, search = "") {
      try {
        const response = await permissionService.fetchPermissionsPaged(
          pageNumber,
          pageSize,
          search
        );
        this.permissions = response.data.data;
        this.totalPermissionsCount = response.data.totalCount;
        this.totalPermissionsPages = response.data.totalPages;
        this.currentPermissionsPage = pageNumber;
        this.permissionSearchTerm = search;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = message;
        console.error("❌ Erro ao buscar permissões paginadas:", message);
        throw new Error(message);
      }
    },

    async getPermissionById(id) {
      try {
        const response = await permissionService.getPermissionById(id);
        this.selectedPermission = response.data;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || error.message;
        console.error("❌ Erro ao obter permissão:", this.error);
        throw new Error(this.error);
      }
    },

    async createPermission(data) {
      try {
        const response = await permissionService.createPermission(data);
        this.shouldForcePermissionsPageOne = true;
        await this.fetchPermissionsPaged(
          1,
          this.permissionsPageSize,
          this.permissionSearchTerm
        );
        return response;
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar permissão:",
          error.message || error
        );
        throw error;
      }
    },

    async updatePermission(permissionId, data) {
      try {
        await permissionService.updatePermission(permissionId, data);

        await this.fetchPermissionsPaged(
          this.currentPermissionsPage,
          this.permissionsPageSize,
          this.permissionSearchTerm
        );
      } catch (error) {
        console.error(
          "❌ Erro ao atualizar permissão:",
          error.message || error
        );
        throw error;
      }
    },

    async deletePermissionById(id) {
      try {
        await permissionService.deletePermission(id);
        await this.fetchPermissionsPaged(
          this.currentPermissionsPage,
          this.permissionsPageSize,
          this.permissionSearchTerm
        );
        if (this.permissions.length === 0 && this.currentPermissionsPage > 1) {
          this.currentPermissionsPage--;
          await this.fetchPermissionsPaged(
            this.currentPermissionsPage,
            this.permissionsPageSize,
            this.permissionSearchTerm
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir permissão:", serverMessage);

        if (serverMessage.includes("Cannot delete or update a parent row")) {
          throw new Error(
            "Não é possível excluir a permissão, pois ela contém textos, atividades ou notas associadas. Remova os vínculos das tabelas associativas antes de tentar excluí-la."
          );
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    async deleteSelectedPermissions() {
      if (!this.selectedPermissionIds.length) return;
      try {
        await permissionService.deletePermissionsBatch(
          this.selectedPermissionIds
        );
        this.clearPermissionSelection();
        await this.fetchPermissionsPaged(
          this.currentPermissionsPage,
          this.permissionsPageSize,
          this.permissionSearchTerm
        );
        if (this.permissions.length === 0 && this.currentPermissionsPage > 1) {
          this.currentPermissionsPage--;
          await this.fetchPermissionsPaged(
            this.currentPermissionsPage,
            this.permissionsPageSize,
            this.permissionSearchTerm
          );
        }
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir permissão:", serverMessage);

        if (serverMessage.includes("Cannot delete or update a parent row")) {
          throw new Error(
            "Não é possível excluir uma ou mais permissões selecionadas, pois elas contêm textos, atividades ou notas associadas. Remova os vínculos das tabelas associativas antes de tentar excluí-las."
          );
        }

        this.error = serverMessage;
        throw new Error(serverMessage);
      }
    },

    setPermissionSearchTerm(term) {
      this.permissionSearchTerm = term;
      this.currentPermissionsPage = 1;
      this.shouldForcePermissionsPageOne = true;
      this.fetchPermissionsPaged(1, this.permissionsPageSize, term);
    },
  },
});
